/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-12-29 10:55:09
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useUserId
} from '../../../app/hooks'
import { fetchGoodList } from '../../../actions/payment'
import { Modal, message } from 'antd'
import classnames from 'classnames'
import sensors from '../../../utils/sensors'
import { capturePromiseException } from '../../../utils/sentryUtils'
import { GoodsItem } from './types'
import CONFIG from '../../../constants/config'
import paymentConfig from '../../../constants/payment'
import useGoodsListAutoScroll from './hooks/useGoodsListAutoScroll'
import useScrollToSelectedItem from './hooks/useScrollToSelectedItem'
import { ExclamationCircleOutlined } from '@ant-design/icons'

const { svipOnlyText } = paymentConfig

interface GoodsListProps {
  selectedGoods: GoodsItem | null
  onChange: any
  sensorClickEvent?: any
}

const GoodsList: React.FC<GoodsListProps> = ({
  selectedGoods,
  onChange,
  sensorClickEvent
}) => {
  const groupID = useAppSelector((state: any) => state.group.groupId)
  const userID = useUserId()
  const dispatch = useAppDispatch()
  const [goodsList, setGoodsList] = useState<GoodsItem[]>([])
  const payment = usePayment()
  const { paymentStatus } = payment

  const goodsListRef = useRef<HTMLDivElement | null>(null)

  const defaultSVIP = useMemo(() => {
    return paymentStatus.vipKind === CONFIG.VIP_KIND.SVIP
  }, [paymentStatus.vipKind])

  const defaultVVIP = useMemo(() => {
    return paymentStatus.vipKind === CONFIG.VIP_KIND.VVIP
  }, [paymentStatus.vipKind])

  const fetchGoodListAndCreateOrderAction = useCallback(() => {
    dispatch(
      fetchGoodList({
        groupID,
        userID
      })
    )
      .then((data) => {
        if (data.status === 0) {
          setGoodsList(data.goods)
          let _selectedGoods = data.goods[0]
          if (defaultSVIP) {
            _selectedGoods =
              data.goods.find(
                (item: GoodsItem) =>
                  item.content.vipKind === CONFIG.VIP_KIND.SVIP
              ) || data.goods[0]
          } else if (defaultVVIP) {
            _selectedGoods =
              data.goods.find(
                (item: GoodsItem) =>
                  item.content.vipKind === CONFIG.VIP_KIND.VVIP
              ) || data.goods[0]
          }
          onChange(_selectedGoods)
          sensorClickEvent('show', _selectedGoods)
        } else {
          message.error(data.msg)
        }
      })
      .catch(capturePromiseException)
  }, [
    groupID,
    userID,
    defaultSVIP,
    defaultVVIP,
    dispatch,
    onChange,
    sensorClickEvent
  ])

  const onGoodsChange = useCallback(
    (item) => {
      sensorClickEvent('vipType', item)

      /**
       * 如果是非vip，点击顶部banner开通按钮(sourceDetail === 'menuResidentEntrance'), 也会自动定位到 svip 商品（ab实验，提升svip的转化率）
       * 针对这种case，允许用户切换vip商品。即选择vip商品时，不弹框提示用户【此权益为超级团队会员】
       */
      // const bailout =
      //   paymentStatus.sourceDetail === 'menuResidentEntrance' && !isVip
      if (
        defaultSVIP &&
        item.content.vipKind !== CONFIG.VIP_KIND.SVIP &&
        paymentStatus.onlySvip
      ) {
        sensors.track('web_get_action_workgroup_vip_need_svip', {
          actionType: 'showPop',
          source_detail: paymentStatus.sourceDetail
        })
        Modal.info({
          centered: true,
          icon: <ExclamationCircleOutlined />,
          title: '提示',
          content:
            svipOnlyText[paymentStatus.sourceDetail] || svipOnlyText.default,
          okText: '我知道了',
          onOk: () => {
            sensors.track('web_get_action_workgroup_vip_need_svip', {
              actionType: 'clickBuy',
              source_detail: paymentStatus.sourceDetail
            })
            onChange(item)
            Promise.resolve()
          }
        })
        return
      } else if (
        item.content.vipKind === CONFIG.VIP_KIND.VIP &&
        paymentStatus.onlyVVip &&
        paymentStatus.sourceDetail !== 'menuResidentEntrance'
      ) {
        Modal.info({
          centered: true,
          icon: <ExclamationCircleOutlined />,
          title: '提示',
          content: '此权益为团队"高级会员"权益，购买基础会员无法享受此权益',
          okText: '我知道了',
          onOk: () => {
            onChange(item)
            Promise.resolve()
          }
        })
        return
      }

      onChange(item)
    },
    [groupID, paymentStatus.sourceDetail]
  )

  useEffect(() => {
    if (groupID) {
      fetchGoodListAndCreateOrderAction()
    }
  }, [groupID, fetchGoodListAndCreateOrderAction])

  // 默认选中的是否是基础会员，用于控制自动滚动展示动画
  const isDefaultVip = useMemo(() => {
    return paymentStatus.vipKind === CONFIG.VIP_KIND.VIP
  }, [paymentStatus.vipKind])

  const autoScrollEnabled = useMemo(() => {
    return isDefaultVip && goodsList.length > 0
  }, [isDefaultVip, goodsList.length])

  const { isScrolling } = useGoodsListAutoScroll(
    goodsListRef,
    autoScrollEnabled,
    [goodsList.length]
  )

  // 当选中商品变化时，滚动到视窗（自动滚动动画执行期间禁用）
  useScrollToSelectedItem(goodsListRef, selectedGoods?.id, {
    disabled: isScrolling
  })

  return (
    <div className="goods-list" ref={goodsListRef}>
      {goodsList.map((item: GoodsItem, index) => {
        return (
          <div className="goods-item" key={index} data-goods-id={item.id}>
            <div
              className={classnames('goods-item-wrapper', {
                active: item.id === selectedGoods?.id,
                'active-svip':
                  item.id === selectedGoods?.id &&
                  item.content.vipKind === CONFIG.VIP_KIND.SVIP
              })}
              onClick={() => onGoodsChange(item)}
            >
              <div
                className={classnames('badge', {
                  'badge-svip': item.content.vipKind === CONFIG.VIP_KIND.SVIP
                })}
              >
                {item.content.sign}
              </div>
              <div className="goods-name">{item.content.title}</div>
              <div className="goods-info">{item.content.subTitle}</div>
              <div className="goods-price">
                <span className="unit">￥</span>
                <span className="num">
                  {parseInt(String(item.content.pricePerMonth))}
                </span>
                <span className="unit" style={{ marginLeft: 5 }}>
                  /月起
                </span>
              </div>
              {/* <div className="goods-suitable">{item.content.footer}</div> */}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default GoodsList
